import type { PointerEvent } from 'react'

import {
  KeyboardCode,
  KeyboardSensor,
  TouchSensor,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core'
import type {
  DroppableContainer,
  KeyboardCoordinateGetter,
  UniqueIdentifier,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'

export function useCustomSensors(): ReturnType<typeof useSensors> {
  const mouseSensor = useSensor(SmartPointerSensor, {
    // Require the mouse to move by 4 pixels before activating
    activationConstraint: { distance: 4 },
  })
  const touchSensor = useSensor(TouchSensor, {
    // Press delay of 250ms, with tolerance of 5px of movement
    activationConstraint: { delay: 250, tolerance: 5 },
  })
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: customSortableKeyboardCoordinates,
  })

  return useSensors(mouseSensor, touchSensor, keyboardSensor)
}

type Identifier = UniqueIdentifier | null | undefined

// This class comes from the dnd-kit library, but it's not exported.
// https://github.com/clauderic/dnd-kit/blob/694dcc2f62e5269541fc941fa6c9af46ccd682ad/packages/core/src/store/constructors.ts#L6
class DroppableContainersMap extends Map<UniqueIdentifier, DroppableContainer> {
  get(id: Identifier): DroppableContainer | undefined {
    return id != null ? (super.get(id) ?? undefined) : undefined
  }

  toArray(): DroppableContainer[] {
    return Array.from(this.values())
  }

  getEnabled(): DroppableContainer[] {
    return this.toArray().filter(({ disabled }) => !disabled)
  }

  getNodeFor(id: Identifier): HTMLElement | undefined {
    return this.get(id)?.node.current ?? undefined
  }
}

const directions: ReadonlySet<string> = new Set([
  KeyboardCode.Down,
  KeyboardCode.Right,
  KeyboardCode.Up,
  KeyboardCode.Left,
])

// Extend the original sortableKeyboardCoordinates function to filter the
// droppable containers to ignore non empty containers when the active item is
// not a container. So when the active item is a "card", for non empty
// containers, we only consider the its children as droppable targets.
const customSortableKeyboardCoordinates: KeyboardCoordinateGetter = (
  event,
  args,
) => {
  const { active, collisionRect, droppableContainers, ...restContext } =
    args.context

  if (!directions.has(event.code)) return

  event.preventDefault()

  if (!active || !collisionRect) {
    return
  }

  const filteredDroppableContainers = new DroppableContainersMap(
    Array.from(droppableContainers.entries()).filter(([_, container]) => {
      return !(
        container.data.current?.type === 'container' &&
        container.data.current.children?.length > 0 &&
        active.data.current?.type !== 'container'
      )
    }),
  )

  return sortableKeyboardCoordinates(event, {
    ...args,
    context: {
      active,
      collisionRect,
      ...restContext,
      droppableContainers: filteredDroppableContainers,
    },
  })
}

/**
 * Grid-aware keyboard coordinate getter for flex-wrap layouts.
 * Sorts items by visual position (Y then X) and navigates by index.
 */
export const gridSortableKeyboardCoordinates: KeyboardCoordinateGetter = (
  event,
  args,
) => {
  const { active, collisionRect, droppableContainers } = args.context

  if (!directions.has(event.code)) return
  event.preventDefault()
  if (!active || !collisionRect) return

  // Get all items sorted by visual grid position (Y then X)
  const items = droppableContainers
    .getEnabled()
    .map((container) => {
      const node = container.node.current
      if (!node) return null
      const rect = node.getBoundingClientRect()
      return { id: container.id, rect }
    })
    .filter(
      (item): item is { id: UniqueIdentifier; rect: DOMRect } => item !== null,
    )
    .toSorted((a, b) => {
      const rowA = Math.round(a.rect.top / 20) // Group by ~20px rows
      const rowB = Math.round(b.rect.top / 20)
      return rowA !== rowB ? rowA - rowB : a.rect.left - b.rect.left
    })

  if (items.length === 0) return

  const currentIdx = items.findIndex((item) => item.id === active.id)
  if (currentIdx === -1) return

  // Calculate items per row from first row
  const firstRowY = Math.round(items[0].rect.top / 20)
  const itemsPerRow = items.filter(
    (item) => Math.round(item.rect.top / 20) === firstRowY,
  ).length

  let targetIdx: number
  switch (event.code) {
    case KeyboardCode.Right:
      targetIdx = currentIdx + 1
      break
    case KeyboardCode.Left:
      targetIdx = currentIdx - 1
      break
    case KeyboardCode.Down:
      targetIdx = currentIdx + itemsPerRow
      break
    case KeyboardCode.Up:
      targetIdx = currentIdx - itemsPerRow
      break
    default:
      return
  }

  const target = items[targetIdx]
  if (!target) return

  // Use target's own dimensions for offset (all grid items are same size)
  return {
    x: target.rect.left,
    y: target.rect.top,
  }
}

/**
 * An extended "PointerSensor" that prevent some
 * interactive html element(button, input, textarea, select, option...) from dragging
 */
export class SmartPointerSensor extends PointerSensor {
  static activators: typeof PointerSensor.activators = [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent: event }: PointerEvent): boolean => {
        const target = event.target instanceof Element ? event.target : null

        if (
          !event.isPrimary ||
          event.button !== 0 ||
          isDraggingDisabled(target) ||
          isInteractiveElement(target) ||
          isBlockingModalOpen(target)
        ) {
          return false
        }

        return true
      },
    },
  ]
}

function isBlockingModalOpen(target: Element | null): boolean {
  const openModals = document.querySelectorAll<HTMLElement>(
    '[role="alertdialog"][data-state="open"], [role="dialog"][data-state="open"]',
  )

  if (openModals.length === 0) {
    return false // No modals open — don't block
  }

  // Block only if the target is inside none of the open modals
  for (const modal of openModals) {
    if (modal.contains(target)) {
      return false // Target is inside an open modal — allow drag
    }
  }

  return true // Modals open but target is outside all of them — block
}

function isDraggingDisabled(element: Element | null): boolean {
  return !!element?.closest('[data-no-dnd]')
}

const interactiveElements = new Set([
  'a',
  'button',
  'input',
  'textarea',
  'select',
  'option',
])

const interactiveRoles = new Set([
  'button',
  'checkbox',
  'link',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'option',
  'radio',
  'slider',
  'switch',
  'tab',
  'textbox',
])

function isInteractiveElement(element: Element | null): boolean {
  if (!element) return false

  function checkElement(el: Element): boolean {
    if (el.hasAttribute('data-dnd')) {
      return false // Allow drag-and-drop for elements with data-dnd attribute
    }

    const isSortable = el.getAttribute('aria-roledescription') === 'sortable'

    // Check tag name first (fastest check)
    if (interactiveElements.has(el.tagName.toLowerCase())) {
      return true
    }

    // Check tabindex next (relatively fast)
    const tabindex = el.getAttribute('tabindex')
    if (tabindex !== null && tabindex !== '-1' && !isSortable) {
      return true
    }

    // Check role last (slowest check)
    const role = el.getAttribute('role')

    return role
      ? interactiveRoles.has(role.toLowerCase()) && !isSortable
      : false
  }

  // Use a do-while loop to check the element and its parents
  let currentElement: Element | null = element
  do {
    if (checkElement(currentElement)) {
      return true
    }
    currentElement = currentElement.parentElement
  } while (currentElement)

  return false
}
